import { useI18n, type MessageKey } from '../i18n';
import { signIn } from '../lib/auth';
import type { Session } from '../lib/session';

// The signed-out screen: what Procure is, how it works, and one sign-in card. No menu and no
// account controls until the person is signed in (Fahad, 2 Nov).

type SignedOut = Exclude<Session, { kind: 'signedIn' }>;

const STEPS = [1, 2, 3] as const;

function SignInCard({ session }: { session: SignedOut }) {
  const { t } = useI18n();
  switch (session.kind) {
    case 'loading':
      return (
        <section class="card signin-card" aria-busy="true">
          <p>{t('signIn.checking')}</p>
        </section>
      );
    case 'notConfigured':
      return (
        <section class="card signin-card" data-testid="signin-unavailable">
          <h2 class="signin-card__title">{t('signIn.title')}</h2>
          <p>{t('signIn.notConfigured')}</p>
        </section>
      );
    case 'signedOut':
      return (
        <section class="card signin-card" aria-labelledby="signin-title">
          <h2 id="signin-title" class="signin-card__title">
            {t('signIn.title')}
          </h2>
          <p>{t('signIn.body')}</p>
          <button
            type="button"
            class="button button--primary signin-card__button"
            onClick={() => void signIn()}
          >
            {t('signIn.button')}
          </button>
          <p class="caption">{t('signIn.guest')}</p>
          <p class="caption signin-card__help">{t('signIn.help')}</p>
        </section>
      );
    case 'error': {
      const known = `error.${session.code}`;
      const message =
        known === 'error.guest_not_allowed' || known === 'error.profile_unavailable'
          ? t(known)
          : t('error.body');
      return (
        <section class="card card--error signin-card" role="alert">
          <h2 class="signin-card__title">{t('error.title')}</h2>
          <p>{message}</p>
          {session.reference && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: session.reference })}
            </p>
          )}
          <button type="button" class="button" onClick={() => location.reload()}>
            {t('error.retry')}
          </button>
        </section>
      );
    }
  }
}

export function SignInPage({ session }: { session: SignedOut }) {
  const { t } = useI18n();
  return (
    <div class="signin">
      <div class="signin__intro">
        <p class="signin__eyebrow">{t('signIn.eyebrow')}</p>
        <h1 class="signin__headline">{t('signIn.headline')}</h1>
        <p class="signin__lead">{t('signIn.intro')}</p>
        <h2 class="visually-hidden">{t('signIn.steps')}</h2>
        <ol class="signin__steps">
          {STEPS.map((n) => (
            <li key={n}>
              <span class="signin__step-no" aria-hidden="true">
                {n}
              </span>
              <span>
                <strong>{t(`signIn.step${n}.title` as MessageKey)}</strong>
                <span class="caption">{t(`signIn.step${n}.body` as MessageKey)}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
      <SignInCard session={session} />
    </div>
  );
}

export function AppFooter() {
  const { t } = useI18n();
  return (
    <footer class="app-footer">
      <div class="app-footer__row">
        <span>{t('footer.copyright', { year: String(new Date().getFullYear()) })}</span>
        <span>{t('footer.internal')}</span>
      </div>
    </footer>
  );
}
